<template>
  <!-- The resume as A4. Two views of the same thing:
       - Continuous (the default): one tall sheet, drawn at its real size (794 px wide) and scaled down to fit.
       - Page breaks: one sheet per printed page, cut where the download cuts them. The cutting is not guessed. The body is laid out in columns as
         tall as the printed page's content area (A4 minus the top and bottom margin, with the same break rules as ResumePrint), which the browser
         fragments with the same engine as when it prints. Each page shows one column.
       A copy of the body laid out that way, off screen, counts the pages (when `countPages` is set, or the page breaks are shown). Without it the count
       is estimated from the sheet's height, which is enough for the small previews on the resume cards. -->
  <div ref="frame" class="w-full">
    <div v-if="!pageBreaks" class="mx-auto" :style="{ width: `${PAGE_WIDTH * scale}px`, height: `${Math.max(height, PAGE_HEIGHT) * scale}px` }">
      <article ref="sheet" class="resume-sheet bg-white text-gray-900 shadow-lg ring-1 ring-black/5 origin-top-left" :style="sheetStyle" :data-template="template">
        <ResumeSheetBody :content="content" :template="template" :picture-url="pictureUrl" />
      </article>
    </div>

    <div v-else class="mx-auto" :style="{ width: `${PRINT_WIDTH * scale}px`, height: `${pageCount * (PRINT_HEIGHT + CAPTION_HEIGHT) * scale}px` }">
      <div class="origin-top-left" :style="{ width: `${PRINT_WIDTH}px`, transform: `scale(${scale})` }">
        <template v-for="n in pageCount" :key="n">
          <div class="resume-page relative bg-white text-gray-900 shadow-lg ring-1 ring-black/5 overflow-hidden" :style="pageStyle" :data-template="template"
            :aria-label="`Page ${n} of ${pageCount}`" :aria-hidden="n > 1 ? 'true' : undefined" :inert="n > 1 ? true : undefined" role="group">
            <div class="absolute overflow-hidden" :style="clipStyle">
              <div class="resume-paged" :style="flowStyle(n - 1)">
                <ResumeSheetBody :content="content" :template="template" :picture-url="pictureUrl" />
              </div>
            </div>
          </div>
          <p class="flex items-center justify-center text-[13px] text-gray-400 dark:text-gray-500 select-none" :style="{ height: `${CAPTION_HEIGHT}px` }" aria-hidden="true">Page {{ n }} of {{ pageCount }}</p>
        </template>
      </div>
    </div>

    <!-- Off screen: the body laid out as pages, only to count them -->
    <div v-if="countPages || pageBreaks" class="fixed -left-[10000px] top-0 invisible pointer-events-none" :style="probeStyle" aria-hidden="true" inert>
      <div ref="probe" class="resume-paged" :style="flowStyle(0)">
        <ResumeSheetBody :content="content" :template="template" :picture-url="pictureUrl" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ResumeSheetBody from './ResumeSheetBody.vue'
import { PAGE_HEIGHT, PAGE_WIDTH, PRINT_HEIGHT, PRINT_WIDTH, horizontalPaddingOf, paddingOf, sheetBackground, typography } from './resumeLayout.js'

const props = defineProps({
  content: { type: Object, required: true },
  template: { type: String, default: 'modern' },
  pictureUrl: { type: String, default: null },
  maxScale: { type: Number, default: 1 }, // 1 never draws the sheet bigger than a printed page; the full screen view allows more
  pageBreaks: { type: Boolean, default: false }, // show one sheet per printed page instead of one continuous sheet
  countPages: { type: Boolean, default: false }, // count the pages exactly, as the print cuts them (the editor does; the cards only estimate)
})
const emit = defineEmits(['pages'])

const CAPTION_HEIGHT = 30 // the space under each page, with its number
const COLUMN_GAP = 40 // between the columns the body is laid out in; only needs to be more than nothing

const frame = ref(null)
const sheet = ref(null)
const probe = ref(null)
const scale = ref(1)
const height = ref(PAGE_HEIGHT)
const pageCount = ref(1)

const settings = computed(() => props.content.settings)
const padding = computed(() => paddingOf(settings.value))
const sidePadding = computed(() => horizontalPaddingOf(props.template, settings.value))

const sheetStyle = computed(() => ({
  ...typography(settings.value),
  width: `${PAGE_WIDTH}px`,
  minHeight: `${PAGE_HEIGHT}px`,
  padding: `${padding.value}px ${sidePadding.value}px`,
  ...sheetBackground(props.template, settings.value),
  transform: `scale(${scale.value})`,
}))

// The same page the download makes (ResumePrint): an A4 sheet whose content has the margin above and below it, on every page.
const pageStyle = computed(() => ({
  ...typography(settings.value),
  width: '210mm',
  height: '297mm',
  ...sheetBackground(props.template, settings.value),
}))
const contentWidth = computed(() => `calc(210mm - ${2 * sidePadding.value}px)`)
const contentHeight = computed(() => `calc(297mm - ${2 * padding.value}px)`)
const clipStyle = computed(() => ({ left: `${sidePadding.value}px`, top: `${padding.value}px`, width: contentWidth.value, height: contentHeight.value }))
const probeStyle = computed(() => ({ ...typography(settings.value), width: contentWidth.value }))

// The body in columns as tall as a page's content area. Column n is page n + 1: sliding the columns left by n columns shows it.
const flowStyle = (index) => ({
  width: contentWidth.value,
  height: contentHeight.value,
  columnWidth: contentWidth.value,
  columnGap: `${COLUMN_GAP}px`,
  columnFill: 'auto',
  transform: index ? `translateX(calc(${-index} * (${contentWidth.value} + ${COLUMN_GAP}px)))` : undefined,
})

// Fit the sheet to the panel, and report how many printed pages it comes to.
function measure() {
  const pageWidth = props.pageBreaks ? PRINT_WIDTH : PAGE_WIDTH
  if (frame.value) scale.value = Math.min(props.maxScale, frame.value.clientWidth / pageWidth) || 1
  if (sheet.value) height.value = sheet.value.offsetHeight

  // The columns the body fills, side by side, from the off screen copy. Without one, the sheet's height says roughly how many.
  const flow = probe.value
  const columns = flow ? Math.round((flow.scrollWidth + COLUMN_GAP) / (flow.clientWidth + COLUMN_GAP)) : Math.ceil((height.value - 4) / PAGE_HEIGHT)
  pageCount.value = Math.max(1, columns || 1)
  emit('pages', pageCount.value)
}

let observer = null
onMounted(() => {
  measure()
  document.fonts?.ready.then(measure)
  if (typeof ResizeObserver === 'undefined') return
  observer = new ResizeObserver(measure)
  observer.observe(frame.value)
  if (sheet.value) observer.observe(sheet.value)
})
onBeforeUnmount(() => {
  observer?.disconnect()
  probe.value?.removeEventListener('load', measure, true)
})

// The off screen copy comes and goes with the props. A picture that has loaded in it can change the height.
watch(probe, (element, before) => {
  before?.removeEventListener('load', measure, true)
  element?.addEventListener('load', measure, true)
  nextTick(measure)
})

// The continuous sheet exists only in that view.
watch(sheet, (element, before) => {
  if (before) observer?.unobserve(before)
  if (element) observer?.observe(element)
})
watch(() => props.content, () => nextTick(measure), { deep: true })
watch(() => [props.template, props.pageBreaks, props.pictureUrl], () => nextTick(measure))
</script>

<style>
/* The rules the download follows (ResumePrint), so that the pages cut here are the pages cut there: an entry is not split across two pages when it
   fits on one, and a heading stays with what follows it. Keep the two in step. */
.resume-paged .resume-entry { break-inside: avoid; }
.resume-paged .resume-section > h2 { break-after: avoid; }
</style>
